import {useCallback} from 'react';
import {captureException} from '@sentry/react';
import {toast} from 'sonner';

import amplitudeService from '@features/analytics/amplitudeService';
import {trpc} from '@features/utils/trpc';
import {useSubscription} from '@features/subscription/subscriptionHook';
import {usePaywallModal} from '@features/subscription/PaywallModal';

export function useExportToFigmaMulti(flowId: string) {
  const {effectiveSubscription} = useSubscription();
  const {openPaywallModal} = usePaywallModal();

  const {mutateAsync: exportMutation, isPending} =
    trpc.figma.exportToFigma.useMutation();

  const exportToFigma = useCallback(
    async (screenIDs: string[]) => {
      if (
        !effectiveSubscription ||
        !effectiveSubscription.isActive ||
        effectiveSubscription.isPastDue
      ) {
        openPaywallModal('export-to-figma');
        return false;
      }

      try {
        amplitudeService.logEvent('[Export button] export to Figma multi', {
          flowId,
          count: screenIDs.length,
        });

        const clipboardItem = new ClipboardItem({
          'text/html': exportMutation({screenIDs}),
        });
        await navigator.clipboard.write([clipboardItem]);

        amplitudeService.logEvent(
          '[Export button] Figma multi export success',
          {
            flowId,
            count: screenIDs.length,
          },
        );

        toast.success('Copied, press "Cmd+V" in Figma');
        return true;
      } catch (error) {
        console.error('Error exporting to Figma:', error);
        captureException(error);
        amplitudeService.logEvent('[Export button] Figma multi export error', {
          flowId,
          error: String(error),
        });
        toast.error('Error exporting to Figma, try again');
        return false;
      }
    },
    [effectiveSubscription, flowId, openPaywallModal, exportMutation],
  );

  return {exportToFigma, isPending};
}
